import { NgTemplateOutlet } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { AppSharedModule } from 'app/app-shared.module';
import { parseNumericInput } from 'app/modules/utilities/shared/decimal-input';
import {
  formatLocalizedNumber,
  getDecimalSeparator,
} from 'app/modules/utilities/shared/localized-number';
import {
  CM_TO_M_FACTOR,
  DEFAULT_NOZZLE_INDEX,
  DEFAULT_PRESSURE_MAX,
  DEFAULT_PRESSURE_MIN,
  DEFAULT_SPACING_CM,
  DEFAULT_TARGET_RATE,
  FLOW_DIGITS,
  LHA_CONVERSION_FACTOR,
  MAX_DUTY_CYCLE,
  MIN_DUTY_CYCLE,
  NOZZLE_TABLE,
  SPEED_DIGITS,
  SprayNozzle,
} from './pwm-spray-calculator.constants';
import {
  positiveNumberValidator,
  pressureRangeValidator,
} from './pwm-spray-calculator.validators';

const TOAST_POSITION = 'toast-top-full-width';

type NumericField = 'targetRate' | 'spacingCm' | 'pressureMin' | 'pressureMax';

/** A nozzle paired with its position in NOZZLE_TABLE, the form's stored value. */
interface NozzleOption {
  index: number;
  nozzle: SprayNozzle;
}

interface SpeedRange {
  minSpeedKmh: number;
  maxSpeedKmh: number;
}

@Component({
  standalone: true,
  selector: 'app-pwm-spray-calculator',
  imports: [
    ReactiveFormsModule,
    NgSelectModule,
    NgTemplateOutlet,
    AppSharedModule,
  ],
  templateUrl: './pwm-spray-calculator.component.html',
  styleUrl: './pwm-spray-calculator.component.scss',
})
export class PwmSprayCalculatorComponent {
  public readonly nozzles: SprayNozzle[] = NOZZLE_TABLE;
  public readonly nozzleOptions: NozzleOption[] = NOZZLE_TABLE.map(
    (nozzle, index) => ({ index, nozzle }),
  );
  public result: SpeedRange | null = null;

  private readonly formBuilder = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly toastr = inject(ToastrService);
  private readonly router = inject(Router);

  public readonly form: FormGroup = this.formBuilder.group(
    {
      nozzleIndex: [DEFAULT_NOZZLE_INDEX, Validators.required],
      targetRate: [
        this.toInputText(DEFAULT_TARGET_RATE),
        [Validators.required, positiveNumberValidator],
      ],
      spacingCm: [
        this.toInputText(DEFAULT_SPACING_CM),
        [Validators.required, positiveNumberValidator],
      ],
      pressureMin: [
        this.toInputText(DEFAULT_PRESSURE_MIN),
        [Validators.required, positiveNumberValidator],
      ],
      pressureMax: [
        this.toInputText(DEFAULT_PRESSURE_MAX),
        [Validators.required, positiveNumberValidator],
      ],
    },
    { validators: pressureRangeValidator },
  );

  constructor() {
    // A result is only valid for the inputs it was calculated from
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.result = null;
    });
  }

  get selectedNozzle(): SprayNozzle | undefined {
    return this.nozzles[Number(this.form.value.nozzleIndex)];
  }

  onCalculate(): void {
    const nozzle = this.selectedNozzle;
    if (this.form.invalid || !nozzle) {
      this.result = null;
      this.warnInvalidForm();
      return;
    }

    const spacingMeters = this.readNumber('spacingCm') / CM_TO_M_FACTOR;
    const targetRate = this.readNumber('targetRate');
    const pressureMin = this.readNumber('pressureMin');
    const pressureMax = this.readNumber('pressureMax');

    const flowCoefficient = nozzle.flow / Math.sqrt(nozzle.pressure);
    const minFlow = flowCoefficient * Math.sqrt(pressureMin) * MIN_DUTY_CYCLE;
    const maxFlow = flowCoefficient * Math.sqrt(pressureMax) * MAX_DUTY_CYCLE;

    this.result = {
      minSpeedKmh:
        (minFlow * LHA_CONVERSION_FACTOR) / (targetRate * spacingMeters),
      maxSpeedKmh:
        (maxFlow * LHA_CONVERSION_FACTOR) / (targetRate * spacingMeters),
    };
  }

  /**
   * Rewrites a valid value with the user's decimal separator when the field
   * loses focus. Invalid or negative input is kept as typed so its error
   * remains visible.
   *
   * @remarks
   * The rewrite is cosmetic: the parsed number is the same before and after
   * (`2.5` → `2,5`, `3,50` → `3,5`). It must not emit `valueChanges`, which
   * clears the result, because Enter submits the form without blurring the
   * field, so the result is often already calculated from this same value.
   */
  normalizeInput(field: NumericField): void {
    const control = this.form.get(field);
    const parsed = parseNumericInput(control?.value);
    if (!control || parsed === null || parsed < 0) {
      return;
    }
    const text = this.toInputText(parsed);
    if (text !== control.value) {
      // Still updates the input on screen and reruns validation, silently
      control.setValue(text, { emitEvent: false });
    }
  }

  formatFlow(value: number): string {
    return formatLocalizedNumber(
      value,
      FLOW_DIGITS,
      this.translateService.currentLang,
    );
  }

  formatSpeed(value: number | undefined): string {
    return formatLocalizedNumber(
      value,
      SPEED_DIGITS,
      this.translateService.currentLang,
    );
  }

  backToUtilities(): void {
    this.router.navigate(['/utilities']);
  }

  /** Only called once the form is valid, so every numeric field parses. */
  private readNumber(field: NumericField): number {
    return parseNumericInput(this.form.value[field]) ?? 0;
  }

  private toInputText(value: number): string {
    return String(value).replace(
      '.',
      getDecimalSeparator(this.translateService.currentLang),
    );
  }

  private warnInvalidForm(): void {
    let messageKey = 'global.validate.fields';
    if (this.form.hasError('pressureRange')) {
      messageKey = 'pwm-calculator-page.validation-pressure-range';
    }
    this.translateService.get(messageKey).subscribe((message: string) => {
      this.toastr.warning(message, '', {
        positionClass: TOAST_POSITION,
        disableTimeOut: false,
      });
    });
  }
}
